/**
 * Setting an invoice import aside, and bringing it back (TASK-0239, FR-RESOLVE).
 *
 * A duplicate upload, a page that is not an invoice, a meat invoice kept only
 * for cost totals: none of them is work for the inbox. Dismissing removes the
 * import from the inbox and from every count; nothing is received, costed or
 * changed. "Show dismissed" lists them again and Restore brings one back.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, errorMessage } from '@/lib/api/client';
import { smartDate } from '@/lib/format';
import { Button } from '@/ui/Button';
import { Callout } from '@/ui/Callout';
import { Dialog } from '@/ui/Dialog';
import { Field, Select, Textarea } from '@/ui/Field';
import { importKeys } from './api';
import { purchasingTodayKeys } from './today/api';
import type { DismissReason, InvoiceImportRow } from './types';

export const DISMISS_REASONS: ReadonlyArray<{ value: DismissReason; label: string }> = [
  { value: 'duplicate', label: 'Duplicate of another upload' },
  { value: 'not_an_invoice', label: 'Not an invoice' },
  { value: 'meat_cost_totals', label: 'Meat: kept for cost totals' },
  { value: 'other', label: 'Something else' },
];

export function dismissReasonLabel(reason: DismissReason | null | undefined): string {
  return DISMISS_REASONS.find((r) => r.value === reason)?.label ?? 'Dismissed';
}

const NOTE_MAX = 500;

export function useDismissImport(importId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { reason: DismissReason; note?: string }) =>
      api.post(`/ai/invoice-imports/${importId}/dismiss`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: importKeys.all });
      // TASK-0250: Today's "invoices waiting" and "photos not read" leave out dismissed imports.
      void queryClient.invalidateQueries({ queryKey: purchasingTodayKeys.all });
    },
  });
}

export function useRestoreImport(importId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/ai/invoice-imports/${importId}/undismiss`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: importKeys.all });
      // TASK-0250: Today's "invoices waiting" and "photos not read" leave out dismissed imports.
      void queryClient.invalidateQueries({ queryKey: purchasingTodayKeys.all });
      toast.success('Restored. It is back in the inbox.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
}

/** Mount only while open, so the form starts empty each time. */
export function DismissDialog({
  importId,
  name,
  onClose,
  onDismissed,
  unsaved = false,
}: {
  importId: number;
  name: string;
  onClose: () => void;
  onDismissed: () => void;
  /** The review pane has line edits that were not saved. */
  unsaved?: boolean;
}) {
  const dismiss = useDismissImport(importId);
  const [reason, setReason] = useState<DismissReason | ''>('');
  const [note, setNote] = useState('');
  const noteNeeded = reason === 'other';
  const ready = reason !== '' && (!noteNeeded || note.trim() !== '');

  function save() {
    if (reason === '' || !ready) return;
    const trimmed = note.trim();
    dismiss.mutate(
      { reason, ...(trimmed ? { note: trimmed } : {}) },
      {
        onSuccess: () => {
          toast.success(`Dismissed ${name}. “Show dismissed” brings it back.`);
          onDismissed();
          onClose();
        },
      }
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !dismiss.isPending) onClose();
      }}
      title="Dismiss this import?"
      description="It leaves the inbox and the counts. Nothing is received or changed, and you can restore it later."
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={dismiss.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!ready}
            loading={dismiss.isPending}
            onClick={save}
          >
            Dismiss
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-ink-muted truncate text-xs">{name}</p>
        {unsaved ? (
          <p className="text-warn text-xs" data-testid="dismiss-unsaved">
            Unsaved changes on this import will be lost.
          </p>
        ) : null}
        <Field label="Why" htmlFor="dismiss-reason" required>
          <Select
            id="dismiss-reason"
            size="sm"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value as DismissReason | '')}
          >
            <option value="">Choose…</option>
            {DISMISS_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Note"
          htmlFor="dismiss-note"
          required={noteNeeded}
          hint={noteNeeded ? 'Say why.' : 'Optional.'}
        >
          <Textarea
            id="dismiss-note"
            rows={2}
            maxLength={NOTE_MAX}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
        </Field>
        {dismiss.isError ? (
          <Callout tone="negative" title="Not dismissed">
            {errorMessage(dismiss.error)}
          </Callout>
        ) : null}
      </div>
    </Dialog>
  );
}

/** What the review pane shows for a dismissed import instead of its lines. */
export function DismissedPanel({
  record,
  restoring,
  onRestore,
}: {
  record: Pick<
    InvoiceImportRow,
    | 'sourceFilename'
    | 'id'
    | 'dismissedAt'
    | 'dismissedReason'
    | 'dismissedNote'
    | 'dismissedByName'
  >;
  restoring: boolean;
  onRestore: () => void;
}) {
  // `smartDate` gives a time for today ("5:54 PM") and a day otherwise ("Oct 12").
  const stamp = record.dismissedAt ? smartDate(record.dismissedAt) : null;
  const when = stamp ? `${/^\d/.test(stamp) ? 'at' : 'on'} ${stamp}` : null;
  return (
    <Callout
      tone="neutral"
      title={`Dismissed: ${dismissReasonLabel(record.dismissedReason)}`}
      actions={
        <Button size="xs" variant="default" loading={restoring} onClick={onRestore}>
          Restore
        </Button>
      }
    >
      <span data-testid="dismissed-note">
        {record.sourceFilename ?? `Import #${record.id}`} was set aside
        {record.dismissedByName ? ` by ${record.dismissedByName}` : ''}
        {when ? ` ${when}` : ''}
        {record.dismissedNote ? `: ${record.dismissedNote}` : '.'} Nothing from it was
        received. Restore it to review it again.
      </span>
    </Callout>
  );
}
